import { ROLES } from "../config/constants";
import { formatDate, formatTime } from "./timeHelpers";
import { calculatePartTimeWorkedRanges } from "./salaryHelpers";

// --- HTML escape 防 XSS（用於匯出 HTML 報表時跳脫使用者輸入） ---
export const escapeHtml = (val) => {
  if (val === null || val === undefined) return "";
  return String(val)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

// --- 核心計算邏輯 ---
export const processLogsWithDuplicates = (rawLogs) => {
  if (!rawLogs || rawLogs.length === 0) return [];
  const logs = [...rawLogs].sort((a, b) => a.dateObj - b.dateObj);
  const processed = logs.map((l) => ({ ...l, isIgnored: false }));

  for (let i = 1; i < processed.length; i++) {
    const prev = processed[i - 1];
    const curr = processed[i];
    const diff = (curr.dateObj - prev.dateObj) / 1000 / 60;

    if (diff <= 5 && prev.type === curr.type) {
      if (curr.type === "in") {
        curr.isIgnored = true;
      } else {
        prev.isIgnored = true;
      }
    }
  }
  return processed;
};

const isTimeCoveredByLeave = (checkTime, leaves) => {
  return leaves.some((leave) => {
    if (leave.status !== "approved") return false;
    const start = new Date(leave.startDate);
    const end = new Date(leave.endDate);
    return checkTime >= start && checkTime < end;
  });
};

const calculateFTDailyStats = (date, dayLogs, dayLeaves) => {
  const validLogs = dayLogs.filter((l) => !l.isIgnored);
  if (validLogs.length === 0)
    return {
      hours: 0,
      ranges: "",
      rawHtml: "",
      lateMinutes: 0,
      isLate: false,
    };

  const stdStart = new Date(date);
  stdStart.setHours(9, 0, 0, 0);
  const lunchStart = new Date(date);
  lunchStart.setHours(12, 0, 0, 0);
  const lunchEnd = new Date(date);
  lunchEnd.setHours(13, 30, 0, 0);

  const firstIn = validLogs.find((l) => l.type === "in");
  validLogs.sort((a, b) => a.dateObj - b.dateObj);

  let lateMinutes = 0;
  let isLate = false;
  let requiredStartTime = new Date(stdStart);

  if (firstIn) {
    const actualIn = firstIn.dateObj;

    let checkTime = new Date(stdStart);
    while (checkTime < actualIn && isTimeCoveredByLeave(checkTime, dayLeaves)) {
      checkTime = new Date(checkTime.getTime() + 60000);
    }
    requiredStartTime = checkTime;

    if (requiredStartTime >= lunchStart && requiredStartTime < lunchEnd) {
      requiredStartTime = new Date(lunchEnd);
    }

    const diffMinutes = Math.floor((actualIn - requiredStartTime) / 60000);

    if (diffMinutes > 0) {
      isLate = true;
      lateMinutes = diffMinutes;
    }
  }

  let totalHours = 0;
  const ranges = [];
  let currentIn = null;
  validLogs.forEach((log) => {
    if (log.type === "in") {
      if (!currentIn) currentIn = log.dateObj;
    } else if (log.type === "out") {
      if (currentIn) {
        let start = currentIn;
        let end = log.dateObj;
        let duration = (end - start) / 60000;

        const olStart = start < lunchStart ? lunchStart : start;
        const olEnd = end > lunchEnd ? lunchEnd : end;

        if (olStart < olEnd) {
          duration -= (olEnd - olStart) / 60000;
        }

        if (duration > 0) {
          totalHours += duration / 60;
          ranges.push(
            `${formatTime(start).slice(0, 5)}-${formatTime(end).slice(0, 5)}`
          );
        }
        currentIn = null;
      }
    }
  });

  const rawHtml = dayLogs
    .map((l) => {
      const timeStr = formatTime(l.dateObj).slice(0, 5);
      const isLateLog = isLate && l === firstIn;
      const style = isLateLog
        ? "color: red; font-weight: bold;"
        : l.isIgnored
        ? "text-decoration: line-through; opacity: 0.5;"
        : "";
      return `<span style="${style}">${timeStr}</span>`;
    })
    .join(" ");

  return {
    hours: parseFloat(totalHours.toFixed(1)),
    ranges: ranges.join(", "),
    rawHtml,
    lateMinutes,
    isLate,
  };
};

const calculatePTDailyStats = (date, dayLogs) => {
  const validLogs = dayLogs.filter((l) => !l.isIgnored);
  if (validLogs.length === 0)
    return {
      hours: 0,
      ranges: "",
      rawHtml: "",
      lateMinutes: 0,
      isLate: false,
    };

  const rawHtml = dayLogs
    .map((l) => {
      const style = l.isIgnored
        ? "text-decoration: line-through; opacity: 0.5;"
        : "";
      return `<span style="${style}">${formatTime(l.dateObj).slice(
        0,
        5
      )}</span>`;
    })
    .join(" ");

  // 共用單一來源 (salaryHelpers)，確保工時頁與薪資頁一致
  const { hours, ranges } = calculatePartTimeWorkedRanges(dayLogs);
  const rangesStr = ranges
    .map(
      (r) =>
        `${formatTime(r.in).slice(0, 5)}-${formatTime(r.out).slice(0, 5)}`
    )
    .join(", ");

  return {
    hours,
    ranges: rangesStr,
    rawHtml,
    lateMinutes: 0,
    isLate: false,
  };
};

export const calculatePeriodStats = (uid, allLogs, leaves, role, year, month) => {
  const monthLogs = allLogs
    .filter(
      (l) =>
        l.dateObj.getMonth() === month &&
        l.dateObj.getFullYear() === year &&
        !l.isIgnored
    )
    .sort((a, b) => a.dateObj - b.dateObj);

  const monthLeaves = leaves.filter((l) => {
    const s = new Date(l.startDate);
    return (
      s.getMonth() === month &&
      s.getFullYear() === year &&
      l.status === "approved"
    );
  });

  const dailyStats = {};
  const logsByDay = {};

  allLogs
    .filter(
      (l) => l.dateObj.getMonth() === month && l.dateObj.getFullYear() === year
    )
    .forEach((l) => {
      const d = formatDate(l.dateObj);
      if (!logsByDay[d]) logsByDay[d] = [];
      logsByDay[d].push(l);
    });

  let totalMonthHours = 0;
  let totalLateCount = 0;
  let totalLateMinutes = 0;
  const leaveSummary = {};

  monthLeaves.forEach((l) => {
    if (!leaveSummary[l.typeName])
      leaveSummary[l.typeName] = { days: 0, hours: 0 };
    leaveSummary[l.typeName].days += parseFloat(l.days);
    leaveSummary[l.typeName].hours += parseFloat(l.hours);
  });

  const todayStr = formatDate(new Date());

  Object.keys(logsByDay)
    .sort()
    .forEach((dateStr) => {
      const dayLogs = logsByDay[dateStr];
      const dateObj = new Date(dateStr);

      let stats;
      if (role === ROLES.FULLTIME) {
        stats = calculateFTDailyStats(
          dateObj,
          dayLogs,
          monthLeaves
        );
      } else {
        stats = calculatePTDailyStats(dateObj, dayLogs);
      }

      totalMonthHours += stats.hours;
      if (stats.isLate) {
        totalLateCount++;
        totalLateMinutes += stats.lateMinutes || 0;
      }

      if (dateStr === todayStr && dayLogs.length % 2 !== 0) {
        stats.isAbnormal = false;
      } else {
        stats.isAbnormal = dayLogs.length % 2 !== 0;
      }

      stats.logs = dayLogs;
      dailyStats[dateStr] = stats;
    });

  return {
    totalHours: totalMonthHours.toFixed(1),
    totalLateCount,
    totalLateMinutes,
    dailyStats,
    leaveSummary,
  };
};


